import { Button, Section, SectionHeader } from "@impero-com/ui";
import { Control, useFieldArray } from "react-hook-form";
import { z } from "zod";

import { RiskDirectoryBlessedFieldIds } from "api/generated/model";

import { HeaderButtons } from "./components/headerButtons";
import { newDefaultSubsection } from "./formData";
import { FormSchema } from "./schema";
import { StructureSubsection, subsectionSchema } from "./subsections";
import * as theme from "./theme.css";

export const sectionLevelSchema = z.literal(["RiskDirectory", "Entity"]);
export type SectionLevel = z.infer<typeof sectionLevelSchema>;

export const sectionSchema = z.object({
    subsections: z.array(subsectionSchema),
    level: sectionLevelSchema,
    sectionId: z.guid(),
});
export type RDStructureSection = z.infer<typeof sectionSchema>;

export type SectionKey = `sections.${number}`;
const MAX_SUBSECTIONS = 2;

interface StructureSectionProps {
    control: Control<FormSchema>;
    sectionKey: SectionKey;
    blessedFieldIds: RiskDirectoryBlessedFieldIds;
}

export function StructureSection({
    control,
    sectionKey,
    blessedFieldIds,
}: StructureSectionProps) {
    const { fields, append, remove, move } = useFieldArray({
        control,
        name: `${sectionKey}.subsections`,
    });

    return (
        <div>
            <div className={theme.subsectionsContainer}>
                {fields.map((subsection, subsectionIndex) => {
                    return (
                        <Section styleVariant="secondary" key={subsection.id}>
                            <SectionHeader
                                level={5}
                                titleLabel={`Subsection ${subsectionIndex + 1}`}
                                showBorder={true}
                                rightChildren={
                                    <HeaderButtons
                                        onClickRemove={(index) => {
                                            remove(index);
                                        }}
                                        onChangeOrder={(index, newOrder) => {
                                            move(index, newOrder);
                                        }}
                                        index={subsectionIndex}
                                        siblingsLength={fields.length}
                                    />
                                }
                            />
                            <StructureSubsection
                                sectionKey={sectionKey}
                                subsectionKey={`${sectionKey}.subsections.${subsectionIndex}`}
                                control={control}
                                blessedFieldIds={blessedFieldIds}
                            />
                        </Section>
                    );
                })}
            </div>
            <Button
                disabled={fields.length >= MAX_SUBSECTIONS}
                onClick={() => append(newDefaultSubsection())}
            >
                + Add subsection
            </Button>
        </div>
    );
}
